import type { Equipment, Presence } from 'shared';

// What the front desk shows, worked out from what the API sends.

export interface EquipmentChoice {
  equipment: Equipment;
  /** Units nobody holds. */
  free: number;
  /** Why it cannot be handed over; null when it can. */
  blocked: string | null;
}

/**
 * The equipment of a centre as choices for one participant: what can be
 * handed to them, and for what cannot, why not. The API decides in the end;
 * this keeps staff from asking for what it would refuse.
 */
export function equipmentChoices(equipments: Equipment[], presence: Presence): EquipmentChoice[] {
  const held = new Set(presence.equipment.map((e) => e.equipmentId));

  return equipments
    .filter((e) => !e.deletedAt && e.digitalCenterID === presence.digitalCenterId)
    .map((equipment) => {
      const free = Math.max(0, equipment.quantidade - equipment.emUso);
      let blocked: string | null = null;
      if (held.has(equipment.id)) blocked = 'Já o tem';
      else if (equipment.estado === 'Inativo') blocked = 'Inativo';
      else if (equipment.estado === 'Reservado') blocked = 'Reservado';
      else if (free === 0) blocked = 'Sem unidades livres';
      return { equipment, free, blocked };
    })
    .sort((a, b) => {
      // What can be handed over first, then by name.
      if ((a.blocked === null) !== (b.blocked === null)) return a.blocked === null ? -1 : 1;
      return a.equipment.equipamento.localeCompare(b.equipment.equipamento, 'pt-PT');
    });
}

/** "2 de 18 livres" / "Livre" / "Em uso" for a single unit. */
export function describeUnits(free: number, total: number): string {
  if (total <= 1) return free > 0 ? 'Livre' : 'Em uso';
  return `${free} de ${total} livres`;
}

/** "14:05" */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}
